import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { Button, StandardDialog } from '@roc/ui';
import { Document } from '@roc/feature-types';
import { useDocumentStore } from '../../../hooks/useDocumentStore';
import { LoanProcess } from '@roc/feature-utils';
import { CustomTaskDialog, TaskV2Type } from '@roc/feature-general-tasks';
import { useExtensionsStore } from '@roc/feature-loan-details';
import { ExtensionDetailsForm } from './extensionDetailsForm';

interface Props {
  open?: boolean;
  onClose?: () => void;
  document: Document;
}

export const ExtensionDetailsDialog = observer(({ open, onClose, document }: Props) => {
  const { documentStore } = useDocumentStore();
  const { extensionsStore } = useExtensionsStore();
  const taskId = document?.taskId;
  const store = documentStore.documentFormStore.extensionDetailsStore;

  useEffect(() => {
    if (open && document?.loanId && document?.extensionId) {
      store.initExtensionDetails(document.loanId, document.extensionId);
    }

    return () => {
      store.reset();
    };
  }, [open, document?.loanId, document?.extensionId]);

  const handleSave = async () => {
    const success = await store.saveExtensionDetails();
    if (success) {
      documentStore.loanTodosStore.fetchTodos(
        document?.loanId,
        LoanProcess.EXTENSIONS,
        TaskV2Type.EXTENSION_ID,
        document?.extensionId
      );

      if (extensionsStore?.refreshSelectedExtension && document?.loanId != null) {
        await extensionsStore.refreshSelectedExtension(String(document.loanId));
      }
      onClose();
    }
  };

  const getActions = () => {
    return (
      <>
        <Button
          color="default"
          onClick={onClose}
          variant="outlined"
          testId='Cancel button'
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          testId='Save button'
          onClick={handleSave}
          disabled={!store.isValid || store.isLoading || store.isReadOnly}
        >
          {store.isLoading ? 'Saving...' : 'Save'}
        </Button>
      </>
    );
  };

  return (taskId ? (
    <CustomTaskDialog
      title={document?.taskName}
      open={open}
      handleClose={onClose}
      dialogContent={<ExtensionDetailsForm store={store} />}
      dialogActions={getActions()}
      taskId={taskId}
    />
  ) : (
    <StandardDialog
      title={document?.taskName}
      open={open}
      handleClose={onClose}
      maxWidth="sm"
      dialogContent={<ExtensionDetailsForm store={store} />}
      dialogActions={getActions()}
    />
  )
  );
});
